import { storyblokEditable } from '@storyblok/react';
import { FC, useEffect, useRef, useState } from 'react';

import RichtextComponent from '@/components/core/RichtextComponent';
import BackgroundMedia from '@/components/elements/BackgroundMedia';
import ButtonArrow, {
  ButtonDirection,
} from '@/components/elements/ButtonArrow';
import Container from '@/components/elements/Container';
import ItemSlider from '@/components/elements/ItemSlider/TimelineSlider';
import Section from '@/components/elements/Section';
import StandardText, {
  StandardTextSize,
} from '@/components/elements/StandardText';

type ItemSliderProps = {
  blok: any;
  className: string;
};

function Timeline({ dates, setDate, selectedDate = 0, sliderWidth = 0 }) {
  const first = new Date(Date.parse(dates[0].date));
  const last = new Date(Date.parse(dates[dates.length - 1].date));

  function selectDate(year) {
    const index = dates.findIndex((d) => {
      const date = new Date(Date.parse(d.date));
      const itemYear = date.getFullYear();

      return itemYear === year;
    });
    setDate(index);
  }

  const firstYear = first.getFullYear();
  const lastYear = last.getFullYear();
  const currentDate = new Date(
    Date.parse(dates[selectedDate].date)
  ).getFullYear();

  const years = Array.from(
    dates.reduce((a, b) => {
      const date = new Date(Date.parse(b.date)).getFullYear();
      a.add(date);
      return a;
    }, new Set())
  );

  const dateWidth = 75;
  const dateSliderWidth = dateWidth * (lastYear - firstYear);
  const currentSliderPos = (lastYear - currentDate) * dateWidth;

  const s = -(dateSliderWidth - currentSliderPos);

  return (
    <div
      className="flex relative mt-4 duration-1000 delay-500"
      style={{
        width: `${dateSliderWidth}px`,
        transform: `translateX(${Math.max(
          s,
          -(dateSliderWidth - sliderWidth) - dateWidth
        )}px)`,
      }}
    >
      <div className="z-0 h-[2px] bg-theme-grey w-full translate-y-[37px] absolute"></div>
      {years.map((d, i) => {
        // @ts-ignore
        const prop: number = ((d - firstYear) / (lastYear - firstYear)) * 100;
        return (
          <div
            key={`datelabel-${i}`}
            className="absolute translate-x-[-10px] translate-y-[-20px] font-semibold font-sans text-[18px] text-[#3c3c3c]"
            style={{
              left: `${prop}%`,
            }}
          >
            {d}
          </div>
        );
      })}
      {years.map((d, i) => (
        <div
          key={`dot-${i}`}
          onClick={() => selectDate(d)}
          className="absolute translate-x-[-10px] cursor-pointer"
          style={{
            left: `${
              // @ts-ignore
              ((d - firstYear) / (lastYear - firstYear)) * 100
            }%`,
          }}
        >
          <div className="absolute translate-y-[28px] translate-x-[9px] rounded-full w-[20px] h-[20px] bg-theme-grey"></div>
        </div>
      ))}
      <div
        className=" rounded-full border-2 border-theme-white absolute translate-y-[23px] translate-x-[-6px] transition-all duration-500 w-[30px] h-[30px] bg-theme-primary"
        style={{
          left: `${
            // @ts-ignore
            ((new Date(Date.parse(dates[selectedDate].date)).getFullYear() -
              firstYear) /
              // @ts-ignore
              (lastYear - firstYear)) *
            100
          }%`,
        }}
      ></div>
    </div>
  );
}

const TimelineSection: FC<ItemSliderProps> = ({
  blok,
  className,
  children,
}) => {
  const [index, setIndex] = useState(0);
  const [sliderWidth, setSliderWidth] = useState(0);
  const slider = useRef<HTMLDivElement>();
  const interval = useRef<any>();
  const container = useRef<HTMLDivElement>();
  const dates = blok.dates.sort(
    (a, b) => Date.parse(a.date) - Date.parse(b.date)
  );

  function move(dir = 0) {
    setIndex((prev) => (blok.dates.length + prev + dir) % blok.dates.length);
  }

  useEffect(() => {
    function getRect() {
      if (slider.current) {
        const rect = slider.current.getBoundingClientRect();
        setSliderWidth(rect.width);
      }
    }

    window.addEventListener('resize', getRect);

    if (slider.current) {
      getRect();
    }

    return () => {
      window.removeEventListener('resize', getRect);
    };
  }, [slider.current]);

  useEffect(() => {
    if (blok.autoAdvance) {
      interval.current = setInterval(() => move(1), blok.delay * 1000);
    }
    return () => {
      clearInterval(interval.current);
    };
  }, []);

  return (
    <Section
      {...storyblokEditable(blok)}
      className={`${className} overflow-hidden`}
    >
      <Container size="wider">
        <div className="xl:grid items-center gap-8 md:grid-cols-[1fr_2fr] xl:md:grid-cols-2 md:gap-16 lg:gap-24">
          <div className="col-span-1">
            <StandardText blok={blok} size={StandardTextSize.MD} />
            {children}
          </div>
          <div>
            <div
              ref={container}
              className="relative xl:w-[47vw] h-fit w-full overflow-hidden bg-theme-black mt-16 xl:mt-0"
            >
              <button
                className={`h-[40px] w-[40px] hidden md:block absolute z-50 left-[20px] top-[50%] -mt-[15px] cursor-pointer rounded-full bg-theme-primary p-[5px] duration-700
                }`}
                onClick={() => move(-1)}
              >
                <ButtonArrow
                  className=" m-1 fill-theme-white"
                  direction={ButtonDirection.LEFT}
                />
              </button>
              <ItemSlider index={index} itemWidth={185} itemGap={0}>
                {dates.map((d, i) => (
                  <div
                    key={`timeline-${i}-${d.date}`}
                    className={`relative transition-all duration-1000 border-r-2 border-theme-gray-medium 
                    ${
                      index === i
                        ? 'w-[80vw] md:w-[min(40vw,calc(634px+40px))]'
                        : 'w-[185px]'
                    } 
                      h-[490px]`}
                  >
                    <BackgroundMedia
                      image={d.image}
                      className={`w-full h-full inset-0 absolute top-0 left-0 -z-20 transition-opacity duration-1000 ${
                        index === i ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    <div className="overflow-hidden w-full inset-0 absolute">
                      <div className="bg-gradient-to-t from-theme-black/80 to-transparent absolute inset-0 -z-10"></div>
                      <div className="absolute inset-0 -z-10"></div>
                      <div className="p-[40px] absolute bottom-0">
                        <div
                          className={`text-white transition-all duration-700 text-shadow-sm shadow-black ${
                            index === i ? 'text-[54px]' : 'text-[40px]'
                          } font-bold font-sans`}
                        >
                          {new Date(Date.parse(d.date)).getFullYear()}
                        </div>
                        <div className="text-white min-w-[350px] lg:max-w-[350px] mt-4 text-shadow-sm shadow-black">
                          <RichtextComponent content={d.content} />
                        </div>
                      </div>
                    </div>

                    <button
                      className={`h-[40px] w-[40px] hidden md:block absolute z-50 right-[-20px] top-[50%] -mt-[20px] cursor-pointer rounded-full bg-theme-primary p-[5px] duration-700 ${
                        index === i ? 'opacity-100' : 'opacity-0'
                      }`}
                      onClick={() => move(1)}
                    >
                      <ButtonArrow
                        className=" m-1 fill-theme-white"
                        direction={ButtonDirection.RIGHT}
                      />
                    </button>
                  </div>
                ))}
              </ItemSlider>
            </div>
            <div className="mt-6 grid w-[120px] grid-cols-2 gap-4">
              <button
                className={`h-[40px] w-[40px] md:hidden cursor-pointer rounded-full border-[1px] border-theme-primary p-[5px] duration-700`}
                onClick={() => move(-1)}
              >
                <ButtonArrow
                  className=" m-1 fill-theme-primary"
                  direction={ButtonDirection.LEFT}
                />
              </button>
              <button
                className={`h-[40px] w-[40px] md:hidden cursor-pointer rounded-full border-[1px] border-theme-primary p-[5px] duration-700`}
                onClick={() => move(1)}
              >
                <ButtonArrow
                  className=" m-1 fill-theme-primary"
                  direction={ButtonDirection.RIGHT}
                />
              </button>
            </div>
            <div
              ref={slider}
              className="xl:w-[47vw] hidden md:block w-full mt-12 h-[72px] md:pl-6 md:pr-8 overflow-hidden"
            >
              <Timeline
                dates={dates}
                setDate={setIndex}
                selectedDate={index}
                sliderWidth={sliderWidth}
              />
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default TimelineSection;
